import { create } from "zustand";
import { bingoApi } from "@/plugins/bingo/services/bingoApi";
import {
  callNext as engineCallNext,
  canUndo as engineCanUndo,
  createDrawState,
  undo as engineUndo,
} from "@/plugins/bingo/engine/drawEngine";
import { sync } from "@/core/sync";
import { BINGO_EVENTS, makeEvent } from "@/core/sync/events";
import { useSettingsStore } from "@/core/state/settingsStore";

/**
 * bingoGameStore
 *
 * Single source of truth for the Bingo plugin on the Host window.
 *
 * Every persistent mutation:
 *   1. Updates local state via the pure drawEngine.
 *   2. Publishes a sync event so the Projector reacts instantly.
 *   3. Fires an auto-save PATCH to the backend so a crash / power cut
 *      never loses a game (Resume Previous Game will restore exactly).
 *
 * Auto-save is fire-and-forget with best-effort logging on failure.
 * Single-user context: sequential PATCHes are safe.
 */

export const GameStatus = Object.freeze({
  IDLE: "IDLE",
  PRE_GAME_TRANSITION: "PRE_GAME_TRANSITION",
  IN_PROGRESS: "IN_PROGRESS",
  PAUSED: "PAUSED",
  REVIEWING: "REVIEWING",
  ANNOUNCING_WINNER: "ANNOUNCING_WINNER",
  GAME_SUMMARY: "GAME_SUMMARY",
});

export const Stage = Object.freeze({
  ONE_LINE: "one_line",
  TWO_LINES: "two_lines",
  FULL_HOUSE: "full_house",
});

// Frontend status enum uses UPPERCASE. Backend uses 3 primary states
// (in_progress | paused | finished). The following mappings are the
// only bridge between the two.
const FE_TO_BE_STATUS = {
  IDLE: "finished",
  PRE_GAME_TRANSITION: "in_progress",
  IN_PROGRESS: "in_progress",
  PAUSED: "paused",
  REVIEWING: "paused",
  ANNOUNCING_WINNER: "paused",
  GAME_SUMMARY: "finished",
};

const BE_TO_FE_STATUS = {
  in_progress: GameStatus.IN_PROGRESS,
  paused: GameStatus.PAUSED,
  finished: GameStatus.GAME_SUMMARY,
};

const emptyWinners = () => ({
  one_line: { name: null, announcedAt: null },
  two_lines: { name: null, announcedAt: null },
  full_house: { name: null, announcedAt: null },
});

const initial = {
  sessionId: null,
  mongoId: null,
  status: GameStatus.IDLE,
  stage: Stage.ONE_LINE,
  drawSequence: [],
  drawIndex: 0,
  called: [],
  undoDepth: 0,
  manualMarks: [],
  mode: "manual",
  autoInterval: 5,
  winners: emptyWinners(),
  pendingWinnerName: "",
  loading: false,
  error: null,
  // ---- Bingo Check · Heartbeat Verification ----
  // Null when not verifying. When active, holds the current
  // verification session so both Host and Projector can render it.
  bingoCheck: null,
  // shape: { numbers: number[], verifiedCount: number, playerLabel: string, bpm: number }

  // ---- Confirmed Bingo Celebration ----
  // Null when idle. When set, the projector runs the hybrid celebration
  // (silence → gold name reveal → confetti/fireworks/brass fanfare).
  confirmedBingo: null,
  // shape: { name: string, stageLabel: string, ts: number }
};

// Heartbeat cadence — starts calm, tightens near the finish.
const HEARTBEAT_CALM_BPM = 60;
const HEARTBEAT_TENSE_BPM = 72;
const TENSE_THRESHOLD = 2; // last N numbers → tense

const publish = (type, payload) =>
  sync.publish(makeEvent(type, payload, "bingo"));

// Module-level timer refs. Cleared on any lifecycle transition.
let summaryAutoDismissTimer = null;
const SUMMARY_AUTO_DISMISS_MS = 12000;

const clearSummaryTimer = () => {
  if (summaryAutoDismissTimer) {
    clearTimeout(summaryAutoDismissTimer);
    summaryAutoDismissTimer = null;
  }
};

const drawStateFrom = (s) =>
  createDrawState({
    drawSequence: s.drawSequence,
    drawIndex: s.drawIndex,
    called: s.called,
    undoDepth: s.undoDepth,
  });

/**
 * Fire a background PATCH with the provided partial fields.
 * Silently ignored if there is no active mongoId (e.g. IDLE state).
 */
const autoSave = async (mongoId, patch) => {
  if (!mongoId) return;
  try {
    await bingoApi.patchSession(mongoId, patch);
  } catch (e) {
    // Never break UX for a save failure.
    console.warn("[autosave] failed", e?.userMessage || e?.message || e);
  }
};

export const useBingoGameStore = create((set, get) => ({
  ...initial,

  // ---------- Introspection ----------
  canUndo: () => engineCanUndo(drawStateFrom(get())),

  // ---------- Lifecycle ----------
  newGame: async () => {
    clearSummaryTimer();
    set({ loading: true, error: null });
    try {
      const active = await bingoApi.getActiveSession().catch(() => null);
      if (active?.id) {
        await bingoApi.patchSession(active.id, { status: "finished" });
      }
      // Pull defaults from the settings store if the game store hasn't
      // been touched yet (fresh app boot → still "manual/5").
      const s = get();
      const cfg = useSettingsStore.getState().settings;
      const session = await bingoApi.createSession({
        mode: s.mode || cfg.defaultMode || "manual",
        autoInterval: s.autoInterval || cfg.defaultAutoInterval || 5,
      });
      set({
        sessionId: session.sessionId,
        mongoId: session.id,
        status: GameStatus.PRE_GAME_TRANSITION,
        stage: Stage.ONE_LINE,
        drawSequence: session.drawSequence,
        drawIndex: 0,
        called: [],
        undoDepth: 0,
        manualMarks: [],
        winners: emptyWinners(),
        pendingWinnerName: "",
        mode: session.mode,
        autoInterval: session.autoInterval,
        loading: false,
        error: null,
      });
      publish(BINGO_EVENTS.NEW_GAME, {
        sessionId: session.sessionId,
        mode: session.mode,
        autoInterval: session.autoInterval,
      });
      setTimeout(() => {
        if (get().status === GameStatus.PRE_GAME_TRANSITION) {
          set({ status: GameStatus.IN_PROGRESS });
        }
      }, 2500);
    } catch (e) {
      set({
        loading: false,
        error: e.userMessage || e.message || "Failed to start game",
      });
    }
  },

  endGame: async () => {
    const s = get();
    set({ status: GameStatus.GAME_SUMMARY, loading: true });
    publish(BINGO_EVENTS.END_GAME, {});
    if (s.mongoId) {
      await autoSave(s.mongoId, { status: "finished" });
    }
    set({ loading: false });
    // Kick off the 12s auto-return-to-welcome timer.
    clearSummaryTimer();
    summaryAutoDismissTimer = setTimeout(() => {
      summaryAutoDismissTimer = null;
      // Only dismiss if we're still on the summary screen.
      if (get().status === GameStatus.GAME_SUMMARY) {
        get().dismissSummary();
      }
    }, SUMMARY_AUTO_DISMISS_MS);
  },

  dismissSummary: () => {
    clearSummaryTimer();
    set({ ...initial });
    publish(BINGO_EVENTS.GAME_SUMMARY_DISMISSED, {});
  },

  resetToIdle: () => {
    clearSummaryTimer();
    set({ ...initial });
  },

  /**
   * Resume Previous Game.
   * Fetches the currently-active session from the backend, hydrates the
   * local store, and publishes a full snapshot so any open Projector
   * catches up. Returns the session, or null if none was active.
   */
  resumeSession: async () => {
    set({ loading: true, error: null });
    try {
      const active = await bingoApi.getActiveSession();
      if (!active || !active.id) {
        set({ loading: false });
        return null;
      }
      set({
        sessionId: active.sessionId,
        mongoId: active.id,
        status: BE_TO_FE_STATUS[active.status] || GameStatus.PAUSED,
        stage: active.stage || Stage.ONE_LINE,
        drawSequence: active.drawSequence || [],
        drawIndex: active.drawIndex || 0,
        called: active.calledNumbers || [],
        undoDepth: active.undoDepth || 0,
        manualMarks: active.manualMarks || [],
        winners: active.winners || emptyWinners(),
        pendingWinnerName: "",
        mode: active.mode || "manual",
        autoInterval: active.autoInterval || 5,
        loading: false,
        error: null,
      });
      // Push full snapshot to any open Projector so it renders the
      // resumed game immediately.
      const s = get();
      sync.publish(
        makeEvent(
          BINGO_EVENTS.STATE_SNAPSHOT,
          {
            sessionId: s.sessionId,
            status: s.status,
            stage: s.stage,
            drawIndex: s.drawIndex,
            called: s.called,
            undoDepth: s.undoDepth,
            manualMarks: s.manualMarks,
            mode: s.mode,
            autoInterval: s.autoInterval,
            winners: s.winners,
          },
          "bingo",
        ),
      );
      publish(BINGO_EVENTS.RESUME_SESSION, { sessionId: s.sessionId });
      return active;
    } catch (e) {
      set({
        loading: false,
        error: e.userMessage || e.message || "Failed to resume game",
      });
      return null;
    }
  },

  // ---------- Calling ----------
  callNext: () => {
    const s = get();
    // Guard: nothing to call in Idle / Pre / Summary / Announcing.
    if (
      s.status === GameStatus.IDLE ||
      s.status === GameStatus.PRE_GAME_TRANSITION ||
      s.status === GameStatus.GAME_SUMMARY ||
      s.status === GameStatus.ANNOUNCING_WINNER
    )
      return;
    if (s.drawIndex >= s.drawSequence.length) return;

    const { state, revealed } = engineCallNext(drawStateFrom(s));
    if (revealed === null) return;
    set({
      drawIndex: state.drawIndex,
      called: state.called,
      undoDepth: state.undoDepth,
      status: GameStatus.IN_PROGRESS,
    });
    publish(BINGO_EVENTS.NUMBER_CALLED, {
      value: revealed,
      drawIndex: state.drawIndex,
      undoDepth: state.undoDepth,
    });
    autoSave(s.mongoId, {
      drawIndex: state.drawIndex,
      calledNumbers: state.called,
      undoDepth: state.undoDepth,
      status: "in_progress",
    });
  },

  undo: () => {
    const s = get();
    if (!engineCanUndo(drawStateFrom(s))) return;
    const { state, removed } = engineUndo(drawStateFrom(s));
    if (removed === null) return;
    set({
      drawIndex: state.drawIndex,
      called: state.called,
      undoDepth: state.undoDepth,
      status: GameStatus.PAUSED,
    });
    publish(BINGO_EVENTS.NUMBER_UNDONE, {
      removed,
      drawIndex: state.drawIndex,
      undoDepth: state.undoDepth,
    });
    autoSave(s.mongoId, {
      drawIndex: state.drawIndex,
      calledNumbers: state.called,
      undoDepth: state.undoDepth,
      status: "paused",
    });
  },

  // ---------- Mode ----------
  setMode: (mode) => {
    const s = get();
    set({ mode });
    publish(BINGO_EVENTS.MODE_CHANGED, { mode });
    autoSave(s.mongoId, { mode });
  },
  setAutoInterval: (autoInterval) => {
    const s = get();
    const clean = [3, 5, 8, 10].includes(autoInterval) ? autoInterval : 5;
    set({ autoInterval: clean });
    publish(BINGO_EVENTS.INTERVAL_CHANGED, { autoInterval: clean });
    autoSave(s.mongoId, { autoInterval: clean });
  },

  // ---------- Pause / Resume ----------
  pause: () => {
    const s = get();
    if (s.status !== GameStatus.IN_PROGRESS) return;
    set({ status: GameStatus.PAUSED });
    publish(BINGO_EVENTS.PAUSED, {});
    autoSave(s.mongoId, { status: "paused" });
  },
  resume: () => {
    const s = get();
    if (s.status !== GameStatus.PAUSED && s.status !== GameStatus.REVIEWING)
      return;
    set({ status: GameStatus.IN_PROGRESS });
    publish(BINGO_EVENTS.RESUMED, {});
    autoSave(s.mongoId, { status: "in_progress" });
  },

  // ---------- Stage ----------
  setStage: (stage) => {
    const s = get();
    set({ stage });
    publish(BINGO_EVENTS.STAGE_CHANGED, { stage });
    autoSave(s.mongoId, { stage });
  },

  // ---------- Winner ----------
  setPendingWinnerName: (name) => set({ pendingWinnerName: name }),

  announceWinner: () => {
    const s = get();
    const now = new Date().toISOString();
    const name = (s.pendingWinnerName || "").trim() || null;
    const nextWinners = {
      ...s.winners,
      [s.stage]: { name, announcedAt: now },
    };
    set({
      winners: nextWinners,
      status: GameStatus.ANNOUNCING_WINNER,
      pendingWinnerName: "",
    });
    publish(BINGO_EVENTS.WINNER_ANNOUNCED, { stage: s.stage, name, at: now });
    autoSave(s.mongoId, {
      winners: nextWinners,
      status: FE_TO_BE_STATUS[GameStatus.ANNOUNCING_WINNER],
    });
  },
  dismissWinner: () => {
    const s = get();
    set({ status: GameStatus.PAUSED });
    publish(BINGO_EVENTS.WINNER_DISMISSED, {});
    autoSave(s.mongoId, { status: "paused" });
  },

  // ---------- Manual mark / unmark ----------
  toggleManualMark: (value) => {
    const s = get();
    const set0 = new Set(s.manualMarks);
    if (set0.has(value)) set0.delete(value);
    else set0.add(value);
    const arr = Array.from(set0).sort((a, b) => a - b);
    set({ manualMarks: arr });
    publish(
      set0.has(value) ? BINGO_EVENTS.MANUAL_MARK : BINGO_EVENTS.MANUAL_UNMARK,
      { value },
    );
    autoSave(s.mongoId, { manualMarks: arr });
  },

  // ---------- Bingo Check · Heartbeat Verification ----------
  /**
   * startBingoCheck — kick off the calm heartbeat verification mode.
   * `numbers` is the claimed line (min 3, max 15). `playerLabel` is
   * optional and shown above the row on the projector.
   *
   * Publishes BINGO_CHECK_STARTED so the projector opens the overlay
   * and starts the heartbeat audio. Never touches called/winners.
   */
  startBingoCheck: (numbers, playerLabel) => {
    const clean = Array.isArray(numbers)
      ? numbers
          .map((n) => Number(n))
          .filter((n) => Number.isFinite(n) && n >= 1 && n <= 90)
          .slice(0, 15)
      : [];
    if (clean.length < 3) return;
    const label =
      typeof playerLabel === "string" && playerLabel.trim().length > 0
        ? playerLabel.trim()
        : "Claimed Line";
    const session = {
      numbers: clean,
      verifiedCount: 0,
      playerLabel: label,
      bpm: HEARTBEAT_CALM_BPM,
    };
    set({ bingoCheck: session });
    publish(BINGO_EVENTS.BINGO_CHECK_STARTED, {
      numbers: clean,
      playerLabel: label,
    });
  },

  /**
   * verifyNextBingoCheck — the host has confirmed the next number is
   * a genuine call. Advances verifiedCount by 1, bumps the heartbeat
   * cadence when only TENSE_THRESHOLD numbers remain, and completes
   * the verification when the last number is confirmed.
   */
  verifyNextBingoCheck: () => {
    const s = get();
    const bc = s.bingoCheck;
    if (!bc) return;
    const total = bc.numbers.length;
    if (bc.verifiedCount >= total) return;

    const nextVerified = bc.verifiedCount + 1;
    const isFinal = nextVerified === total;
    const remainingAfter = total - nextVerified;
    const nextBpm =
      !isFinal && remainingAfter > 0 && remainingAfter <= TENSE_THRESHOLD
        ? HEARTBEAT_TENSE_BPM
        : bc.bpm;

    set({
      bingoCheck: { ...bc, verifiedCount: nextVerified, bpm: nextBpm },
    });
    publish(BINGO_EVENTS.BINGO_CHECK_VERIFIED, {
      verifiedCount: nextVerified,
      bpm: nextBpm,
    });

    if (isFinal) {
      // Final confirm. Signal completion so the projector cuts the
      // heartbeat instantly. After a brief silence, transition into
      // the Confirmed Bingo Celebration (hybrid: gold reveal → burst).
      publish(BINGO_EVENTS.BINGO_CHECK_COMPLETED, {});
      setTimeout(() => {
        const cur = get();
        if (cur.bingoCheck?.verifiedCount !== total) return;
        // Compose the stage label from current stage.
        const stageLabel =
          cur.stage === Stage.FULL_HOUSE
            ? "FULL HOUSE WINNER"
            : cur.stage === Stage.TWO_LINES
              ? "2 LINES WINNER"
              : "1 LINE WINNER";
        const celebration = {
          name: bc.playerLabel && bc.playerLabel !== "Claimed Line"
            ? bc.playerLabel
            : "",
          stageLabel,
          ts: Date.now(),
        };
        // Record the winner in the game store's winners map so the
        // existing recap ledger stays correct.
        const nowIso = new Date().toISOString();
        const nextWinners = {
          ...cur.winners,
          [cur.stage]: {
            name: celebration.name || null,
            announcedAt: nowIso,
          },
        };
        set({
          bingoCheck: null,
          confirmedBingo: celebration,
          winners: nextWinners,
        });
        publish(BINGO_EVENTS.CONFIRMED_BINGO_STARTED, celebration);
        autoSave(cur.mongoId, { winners: nextWinners });
      }, 400); // 0.2-0.3s silence per spec (slight buffer)
    }
  },

  /**
   * cancelBingoCheck — host aborted the verification (mis-claim,
   * wrong ticket, etc.). Instantly closes the overlay on all screens.
   */
  cancelBingoCheck: () => {
    if (!get().bingoCheck) return;
    set({ bingoCheck: null });
    publish(BINGO_EVENTS.BINGO_CHECK_CANCELLED, {});
  },

  /**
   * dismissConfirmedBingo — host clicks Continue (or the auto-return
   * timer fires) to end the celebration and return to the show.
   */
  dismissConfirmedBingo: () => {
    if (!get().confirmedBingo) return;
    set({ confirmedBingo: null });
    publish(BINGO_EVENTS.CONFIRMED_BINGO_DISMISSED, {});
  },
}));
